// @ts-nocheck
// Adapted from tailuge/billiards (GPL-3.0) — src/model/table.ts
// Modified: view-free engine. Removed Cue / ProximityIndicator / AimEvent /
// mesh / serialisation-for-network. Keeps the proven collision-resolution loop.
import { Cushion } from "./cushion"
import { Collision } from "./collision"
import { Knuckle } from "./knuckle"
import { Pocket } from "./pocket"
import { Ball, State } from "./ball"
import { TableGeometry } from "./tablegeometry"
import { PocketGeometry } from "./pocketgeometry"
import { bounceHanBlend } from "./physics"
import { zero } from "./three-utils"

export interface SimOutcome {
  type: "collision" | "cushion" | "pot"
  a: number
  b: number
  speed: number
}

interface Pair {
  a: Ball
  b: Ball
}

export class Table {
  balls: Ball[]
  pairs: Pair[]
  outcome: SimOutcome[] = []
  cueball: Ball
  cushionModel = bounceHanBlend

  constructor(balls: Ball[]) {
    this.cueball = balls[0]
    this.initialiseBalls(balls)
  }

  initialiseBalls(balls: Ball[]) {
    this.balls = balls
    this.cueball = balls[0] // リセット時に手球参照を更新（重要）
    this.pairs = []
    for (let a = 0; a < balls.length; a++) {
      for (let b = 0; b < balls.length; b++) {
        if (a < b) {
          this.pairs.push({ a: balls[a], b: balls[b] })
        }
      }
    }
  }

  advance(t: number) {
    let depth = 0
    while (!this.prepareAdvanceAll(t)) {
      if (depth++ > 100) {
        throw new Error("Depth exceeded resolving collisions")
      }
    }
    this.balls.forEach((a) => {
      a.update(t)
      a.fround()
    })
  }

  prepareAdvanceAll(t: number) {
    return (
      this.pairs.every((pair) => this.prepareAdvancePair(pair.a, pair.b, t)) &&
      this.balls.every((ball) => this.prepareAdvanceToCushions(ball, t))
    )
  }

  private prepareAdvancePair(a: Ball, b: Ball, t: number) {
    if (Collision.willCollide(a, b, t)) {
      const incidentSpeed = Collision.collide(a, b)
      this.outcome.push({
        type: "collision",
        a: a.id,
        b: b.id,
        speed: incidentSpeed,
      })
      return false
    }
    return true
  }

  private prepareAdvanceToCushions(a: Ball, t: number): boolean {
    if (!a.onTable()) {
      return true
    }
    const futurePosition = a.futurePosition(t)
    if (
      Math.abs(futurePosition.y) < TableGeometry.tableY &&
      Math.abs(futurePosition.x) < TableGeometry.tableX
    ) {
      return true
    }

    const incidentSpeed = Cushion.bounceAny(
      a,
      t,
      TableGeometry.hasPockets,
      this.cushionModel
    )
    if (incidentSpeed) {
      this.outcome.push({ type: "cushion", a: a.id, b: a.id, speed: incidentSpeed })
      return false
    }

    const k = Knuckle.findBouncing(a, t)
    if (k) {
      const knuckleIncidentSpeed = k.bounce(a)
      this.outcome.push({
        type: "cushion",
        a: a.id,
        b: a.id,
        speed: knuckleIncidentSpeed,
      })
      return false
    }

    const p = Pocket.findPocket(PocketGeometry.pocketCenters, a, t)
    if (p) {
      const pocketIncidentSpeed = p.fall(a, t)
      this.outcome.push({ type: "pot", a: a.id, b: a.id, speed: pocketIncidentSpeed })
      return false
    }

    return true
  }

  allStationary() {
    return this.balls.every((b) => !b.inMotion())
  }

  inPockets(): number {
    return this.balls.reduce((acc, b) => (b.onTable() ? acc : acc + 1), 0)
  }

  halt() {
    this.balls.forEach((b) => {
      b.vel.copy(zero)
      b.rvel.copy(zero)
      b.state = State.Stationary
    })
  }

  drainOutcomes(): SimOutcome[] {
    const o = this.outcome
    this.outcome = []
    return o
  }
}
